import type { ComparisonResultItem, ValueWithStatus } from '../model/ComparisonResult.type'
import { ComparisonStatus } from '../model/ComparisonStatus.enum'

function compareObjects<T extends object, U extends object>(
  objA: T,
  objB: U,
): ComparisonResultItem<T & U>[] {
  if (!objA || !objB) return []

  const allKeys = new Set([...Object.keys(objA), ...Object.keys(objB)])
  const results: ComparisonResultItem<T & U>[] = []

  allKeys.forEach((key) => {
    const valueA = objA[key]
    const valueB = objB[key]

    if (!(key in objA)) {
      results.push({ key, status: ComparisonStatus.MissingInA, valueB })
    } else if (!(key in objB)) {
      results.push({ key, status: ComparisonStatus.MissingInB, valueA })
    } else if (valueA === valueB) {
      results.push({ key, status: ComparisonStatus.Identical, valueA, valueB })
    } else if (valueA === '' || valueA === null || valueA === undefined) {
      results.push({ key, status: ComparisonStatus.EmptyInA, valueA, valueB })
    } else if (valueB === '' || valueB === null || valueB === undefined) {
      results.push({ key, status: ComparisonStatus.EmptyInB, valueA, valueB })
    } else {
      results.push({ key, status: ComparisonStatus.Different, valueA, valueB })
    }
  })

  return results
}

function hasDifferencesOnKeys<T>(
  result: ComparisonResultItem<T>[],
  keys: (keyof T | string)[],
): boolean {
  return result.some(
    (item) =>
      keys.includes(item.key) &&
      [
        ComparisonStatus.Different,
        ComparisonStatus.EmptyInA,
        ComparisonStatus.EmptyInB,
        ComparisonStatus.MissingInA,
        ComparisonStatus.MissingInB,
      ].includes(item.status),
  )
}

function extractValueWithStatus<T extends object>(
  results: ComparisonResultItem<T>[],
  source: 'valueA' | 'valueB',
): Record<keyof T | string, ValueWithStatus> {
  return results.reduce(
    (acc, item) => {
      acc[item.key] = {
        value: item[source] as string,
        status: item.status,
      }
      return acc
    },
    {} as unknown as Record<keyof T | string, ValueWithStatus>,
  )
}

function castValue<T>(value: unknown): T | undefined {
  return value as T | undefined
}

export { compareObjects, extractValueWithStatus, hasDifferencesOnKeys, castValue }
